/* Training Scripts — teleprompter-ready 60–90 second scripts for Mike to
   record alongside Lesson 1–6 of each platform-tool course.

   Format:
     - script: full read-aloud text (Mike reads top to bottom)
     - chunks: 3-word chips for typewriter caption sync
     - audioUrl: filled in by main agent after Mike sends MP3
     - imageUrl: defaults to Mike headshot (one shared image is fine)

   Pacing math (auto-sync, no LLM):
     chip_duration_ms = audio_duration_ms / chunks.length

   Course 1 (Funnel Builder 101) is fully scripted.
   Other 5 courses will be scripted in next session(s) once Mike approves
   the voice + the player feels right. */

const MIKE_HEADSHOT = "/mike/mike-headshot.png";  // upload to /public/mike/

/* Helper — auto-chunk a script string into ~3-word caption chips.
   Splits on whitespace, groups 3 at a time, preserves punctuation. */
export function chunkScript(text, wordsPerChip = 3) {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  const out = [];
  for (let i = 0; i < words.length; i += wordsPerChip) {
    out.push(words.slice(i, i + wordsPerChip).join(" "));
  }
  return out;
}

export const TRAINING_SCRIPTS = {
  /* ─────────── Course 01 — Funnel Builder 101 ─────────── */
  "funnel-builder-101": {
    headshot: MIKE_HEADSHOT,
    lessons: {
      1: {
        title: "What A Funnel Is (Plain English)",
        script:
          "Hey, friend. It's Mike Boyd. Listen — I want to clear something up before " +
          "you ever touch the Faitharama Funnel Builder. A funnel ain't fancy. A funnel " +
          "is just a page that walks one person, one step at a time, toward one " +
          "decision. That's it. The headline grabs them. The picture builds trust. " +
          "A few bullets show the value. And the button collects either their email, " +
          "their money, or both. Folks been building these things on a single piece of " +
          "paper for centuries — we just made it scroll. You do NOT need five pages. " +
          "You do NOT need a webinar. For most of our members, ONE good funnel page " +
          "is what makes the first sale. Keep it simple. Keep it real. Let's build.",
        audioUrl: null,
        durationSec: 80,
      },
      2: {
        title: "Opening The Builder",
        script:
          "Alright. From your Dashboard, look down the left sidebar. You'll see a " +
          "tab called Funnels. Click it. Then hit the big NEW FUNNEL button at the " +
          "top of the page. Give your funnel a name only YOU will ever see — " +
          "something like 'Goli Gummies for Aunt Carol.' Pick a starting template. " +
          "If this is your first one, I want you to pick Lead Capture. Trust me. " +
          "Now the builder opens up. Take a breath. Look at it. On the left side " +
          "you got your building blocks. In the middle is your live page — that's " +
          "what your visitors are gonna see. On the right is the settings panel — " +
          "whatever you tap on shows its settings over there. That's the whole " +
          "machine. Three panels. You got this.",
        audioUrl: null,
        durationSec: 80,
      },
      3: {
        title: "The 6 Blocks You'll Actually Use",
        script:
          "There's a bunch of blocks in that left panel and most of them you'll " +
          "never need. Here's the six I want you to use. Number one — Headline. " +
          "One sentence. Big bold words. What does the visitor get? Number two — " +
          "Image or Video. Use a real photo. Your face beats stock art every time. " +
          "Number three — Bullet List. Three to five short benefits, each one starts " +
          "with a verb. Number four — Buy or Lead Button. Either pulls their money " +
          "or pulls their email. Number five — Testimonial. One real quote from one " +
          "real person. Be honest. Number six — Spacer. Empty space. Easy on senior " +
          "eyes. Use it generously. Six blocks. That's the whole game.",
        audioUrl: null,
        durationSec: 85,
      },
      4: {
        title: "Adding Your Image (Step By Step)",
        script:
          "Click any Image block on your page. Over on the right you'll see a " +
          "gray Upload button. Click it. Pick the photo off your computer or off " +
          "your phone. Wait about three seconds. Boom — there's your picture, " +
          "right on the page. If it's too big or too small, grab the corner and " +
          "drag it till it looks right. Now listen to me. The picture matters " +
          "more than you think. A smiling face beats a fancy logo every single " +
          "time. Use a photo of YOU. Use a photo of your kitchen. Your Bible. " +
          "Your dog. Anything real. Stock photos make folks scroll. Real photos " +
          "make folks read. That's the rule.",
        audioUrl: null,
        durationSec: 75,
      },
      5: {
        title: "Wiring The Buy Button",
        script:
          "Click your button block. Over on the right, you'll see a little " +
          "dropdown — Pulls money, or Pulls email. If you want them to buy, " +
          "pick Pulls money — Buy Button. Then choose the product from the " +
          "dropdown right under it. Every Faitharama product is already on " +
          "that list — books, supplements, courses, certifications. Pick yours. " +
          "Hit Save. You're done. Stripe handles the credit card. Faitharama " +
          "handles the delivery. The money lands in YOUR account. Now if you " +
          "want their email instead — to grow your list before you sell them " +
          "anything — pick Pulls email — Lead Button. Their email lands in " +
          "your Contacts tab automatically. Either way, it's two clicks.",
        audioUrl: null,
        durationSec: 85,
      },
      6: {
        title: "Publishing And Sharing",
        script:
          "Last step. Top right of the builder, you'll see a big green PUBLISH " +
          "button. Click it. Your funnel is now live on the internet. You'll get " +
          "a link that looks like faitharama.com/f/your-name/your-funnel. Copy " +
          "that link. Text it to your sister. Post it on Facebook. Stick it in " +
          "your YouTube description. Email it to your church group. Every visit, " +
          "every click, every sale gets tracked under YOUR account — automatic. " +
          "Now hear this, friend. One funnel done well will outpull twenty " +
          "funnels done halfway. Don't build six funnels in your first week. " +
          "Build ONE. Share it everywhere. Watch what happens. Then build the " +
          "next one. Slow is smooth. Smooth is fast. God bless.",
        audioUrl: null,
        durationSec: 90,
      },
    },
  },
};

/* For lessons we haven't scripted yet, the player just shows the headshot
   placeholder and a "Script coming soon — read the lesson below" note. */
export function getScript(courseSlug, lessonNumber) {
  const course = TRAINING_SCRIPTS[courseSlug];
  if (!course) return null;
  return course.lessons[lessonNumber] || null;
}

export function getHeadshot(courseSlug) {
  return TRAINING_SCRIPTS[courseSlug]?.headshot || MIKE_HEADSHOT;
}
